import React, { type FC, useMemo } from 'react';
import { formatDateTime } from 'utils/date';

import { Amount } from '@fragment/ui/src/components/Amount/Amount';
import { Icon } from '@fragment/ui/src/components/Icon';
import { IdCopyButton } from '@fragment/ui/src/components/IdCopyButton';
import { NavTable } from '@fragment/ui/src/components/Table';
import { TableHoverDetailRow } from '@fragment/ui/src/components/TableHoverDetailRow/TableHoverDetailRow';

import type { TxNode } from '../types';

const emptyValue = {
  Transaction: 'No Transactions',
  Timestamp: 'N/A',
  Amount: 'N/A',
};

type TxsTableProps = {
  data: TxNode[];
  selected?: TxNode;
  getPath: (row: TxNode) => string;
  hasNextPage?: boolean;
  loadMore: VoidFunction;
  isLoading?: boolean;
  ledgerAccountId: string | undefined;
};

export const TxsTable: FC<TxsTableProps> = ({
  data,
  selected,
  getPath,
  hasNextPage,
  loadMore,
  isLoading,
  ledgerAccountId,
}) => (
  <NavTable<TxNode>
    textSize="md"
    borderedRows
    dottedHeader={false}
    {...{
      data,
      selected,
      schema: useMemo(
        () => [
          {
            title: 'Transaction',
            // There's no way to get this to work in a way that react wants, because we depend
            // on values in props to construct the component.
            // The component definition is stable, becase this whole thing is `useMemo`d.
            // eslint-disable-next-line react/no-unstable-nested-components
            format: (row) => {
              const isReconciled = !!row.ledgerLines.nodes.find(
                (value) => value.accountId === ledgerAccountId
              );
              return (
                <TableHoverDetailRow
                  detail={
                    <IdCopyButton
                      type="externalId"
                      identifier={row.externalId}
                    />
                  }
                >
                  <div className="h-f2 flex items-center">
                    <Icon
                      type={isReconciled ? 'reconciledTx' : 'unreconciledTx'}
                    />
                  </div>

                  <div className="truncate">{row.description}</div>
                </TableHoverDetailRow>
              );
            },
            colspan: 'col-span-2',
          },
          {
            title: 'Posted',
            // eslint-disable-next-line react/no-unstable-nested-components
            format: ({ posted }) => (
              <span>{posted ? formatDateTime(posted) : 'N/A'}</span>
            ),

            colspan: 'col-span-2',
          },
          {
            title: 'Amount',
            justify: 'right',
            // eslint-disable-next-line react/no-unstable-nested-components
            format: ({ amount, currency }) => (
              <Amount amount={amount} currency={currency!} />
            ),
          },
        ],
        [ledgerAccountId]
      ),
      getPath,
      emptyValue,
      hasNextPage,
      loadMore,
      isLoading,
    }}
  />
);
